import AddItemForm from '@components/drawers/AddItemForm';
import CustomDrawer from '@components/drawers/CustomDrawer';
import GridListSubheader from '@components/GridListSubheader';
import ItemWithSummaryDrag from '@components/ItemWithSummaryDrag';
import WolfHelperNavigation from '@components/ui/WolfHelperNavigation';
import { graphQlClient } from '@config/graphqlClient';
import AreasGraphQL from '@graphql/area.queries';
import IntegrationsGraphQL from '@graphql/integration.queries';
import ServiceGraphQL from '@graphql/service.queries';
import { useLoader } from '@hooks/useLoader';
import { useLocationQuery } from '@hooks/useLocationQuery';
import { useTranslation } from '@hooks/useTranslation';
import { Area, getAreaByIDRequest, getAreasRequest } from '@models/area.model';
import { DrawerType, EntityData, SnackType } from '@models/common.model';
import {
  createOrUpdateServiceRequest,
  deleteServiceRequest,
  getServicesRequest,
  reorderServicesRequest,
  Service
} from '@models/service.model';
import { UserRoles } from '@models/user.model';
import { Box, CircularProgress } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useAppDispatch, useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { RootState } from '@redux/store';
import { checkForIntegrations } from '@utils/checkForIntegrations';
import { DropResult, notValidDrop } from '@utils/notValidDrop';
import { useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';

const ServiceList: React.FC = () => {
  const dispatch = useAppDispatch();
  const history = useHistory();
  const { setLoading } = useLoader();

  const localeServices = useTranslation('services');
  const localeCommon = useTranslation('common');
  const localeActionMessages = useTranslation('actionMessages');

  const [areas, setAreas] = useState<Area[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [selectedArea, setSelectedArea] = useState<Area>();
  const [showDrawer, setShowDrawer] = useState(false);
  const [drawerType, setDrawerType] = useState<DrawerType>(DrawerType.ADD);
  const [entityData, setEntityData] = useState<EntityData>({
    areaId: '',
    name: '',
    description: '',
    color: '',
  });
  const [hasCalendar, setHasCalendar] = useState(false);
  const [hasStorage, setHasStorage] = useState(false);
  const [saving, setSaving] = useState(false);
  const query = useLocationQuery();
  const [isGridView, setIsGridView] = useState(true);
  const cardRefs = useRef<{ [key: string]: any | null }>({});
  const [selectedNavigationItem, setSelectedNavigationItem] = useState<any>();
  const [autoOpenDrawer, setAutoOpenDrawer] = useState(false);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [recentlyDroppedId, setRecentlyDroppedId] = useState<string | null>(null);

  const { role } = useAppSelector((state: RootState) => state.user);

  // Get params & get services by areaId or all services if no ID
  useEffect(() => {
    const areaId = query.get('areaId') ?? '';
    const openDrawer = query.get('openDrawer') === 'true';

    // Store auto-open state but don't execute yet
    setAutoOpenDrawer(openDrawer);
    setDataLoaded(false);

    if (!areaId || areaId === 'undefined') {
      getServices();
      getAreas();
    } else {
      getServicesByAreaID(areaId);
    }
    getIntegrations();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  // Auto-open drawer after data is loaded
  useEffect(() => {
    if (autoOpenDrawer && dataLoaded) {
      drawerNewOpen();
      setAutoOpenDrawer(false); // Reset to prevent multiple opens
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenDrawer, dataLoaded]);

  const handleCardClick = async (id: string, e?: React.MouseEvent) => {
    const url = `/processes?serviceId=${id}`;
    if (e && (e.ctrlKey || e.metaKey)) {
      window.open(url, '_blank');
    } else {
      history.push(url);
    }
  };

  const handleToggleView = () => {
    setIsGridView(!isGridView);
  };

  // DRAWER HANDLE *********************
  const drawerNewOpen = async () => {
    if (areas.length === 0 && !selectedArea) {
      dispatch(
        appendActionMessage({
          message: localeServices['createAreaFirst'],
          type: SnackType.WARNING,
        }),
      );
      return;
    }

    setDrawerType(DrawerType.ADD);
    setShowDrawer(true);
  };

  const drawerEditOpen = (id: string) => {
    setDrawerType(DrawerType.EDIT);
    setShowDrawer(true);
    const serviceToEdit = services.find((service: Service) => service.serviceId === id);
    if (serviceToEdit) {
      setEntityData(serviceToEdit);
    }
  };

  const drawerClose = (): void => {
    setShowDrawer(false);
  };

  // Drag and Drop
  const handleDragEnd = async (result: DropResult) => {
    let newServices: Service[] = [];
    if (notValidDrop(result)) return;
    if (!selectedArea) {
      const sourceAreaId = services.filter((item) => item.serviceId === result.draggableId)[0].areaId;
      const servicesInArea = [...services.filter((item) => item.areaId === sourceAreaId)];
      const servicesNotInArea = services.filter((item) => item.areaId !== sourceAreaId);

      // Reorder only services in the same area using insert logic
      const [movedItem] = servicesInArea.splice(result.source.index, 1);
      servicesInArea.splice(result.destination!.index, 0, movedItem);

      // Combine reordered services with unchanged services from other areas
      newServices = [...servicesInArea, ...servicesNotInArea];

      // Only send the IDs of services in the affected area for reordering
      const servicesIds = servicesInArea.map((item: Service) => item.serviceId);

      try {
        const data: reorderServicesRequest = await graphQlClient.request(ServiceGraphQL.mutations.reorderServices, {
          services: { servicesIds },
        });
        if (data?.reorderServices.messages[0] === 'Update successful') {
          setServices(newServices);

          // Set recently dropped item and clear after 5 seconds
          setRecentlyDroppedId(result.draggableId);
          setTimeout(() => {
            setRecentlyDroppedId(null);
          }, 5000);

          dispatch(
            appendActionMessage({
              message: localeActionMessages['servicesReorderedSuccessfully'],
              type: SnackType.SUCCESS,
            }),
          );
        }
      } catch (e: any) {
        console.log('error', e);
        dispatch(
          appendActionMessage({
            message: e?.response?.errors[0]?.message || localeCommon['requestError'],
            type: SnackType.ERROR,
          }),
        );
      }
    } else {
      // When a specific area is selected, reorder all services using insert logic
      newServices = [...services];
      const [movedItem] = newServices.splice(result.source.index, 1);
      newServices.splice(result.destination!.index, 0, movedItem);

      const servicesIds = newServices.map((item: Service) => item.serviceId);
      try {
        const data: reorderServicesRequest = await graphQlClient.request(ServiceGraphQL.mutations.reorderServices, {
          services: { servicesIds },
        });
        if (data?.reorderServices.messages[0] === 'Update successful') {
          setServices(newServices);

          // Set recently dropped item and clear after 5 seconds
          setRecentlyDroppedId(result.draggableId);
          setTimeout(() => {
            setRecentlyDroppedId(null);
          }, 5000);

          dispatch(
            appendActionMessage({
              message: localeActionMessages['servicesReorderedSuccessfully'],
              type: SnackType.SUCCESS,
            }),
          );
        }
      } catch (e: any) {
        console.log('error', e);
        dispatch(
          appendActionMessage({
            message: e?.response?.errors[0]?.message || localeCommon['requestError'],
            type: SnackType.ERROR,
          }),
        );
      }
    }
  };

  // CRUD Operations (incl Drawer save new/edit)
  const getServices = async () => {
    try {
      setLoading(true);
      setHasError(false);
      const data: getServicesRequest = await graphQlClient.request(ServiceGraphQL.queries.getServices);
      setServices(data.getServices.sort((a, b) => (a.order > b.order ? 1 : -1)));
      setDataLoaded(true);
    } catch (e: any) {
      setHasError(true);
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const getAreas = async () => {
    try {
      setHasError(false);
      const data: getAreasRequest = await graphQlClient.request(AreasGraphQL.queries.getAreas);
      setAreas(data.getAreas.sort((a, b) => (a.order > b.order ? 1 : -1)));
    } catch (e: any) {
      setHasError(true);
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const getServicesByAreaID = async (id: string) => {
    try {
      setLoading(true);
      setHasError(false);
      const variables = { id };
      const data: getAreaByIDRequest = await graphQlClient.request(AreasGraphQL.queries.getAreaByID, variables);
      setServices(data?.getAreaByID?.services.sort((a, b) => (a.order > b.order ? 1 : -1)));
      setSelectedArea(data?.getAreaByID);
      setDataLoaded(true);
    } catch (e: any) {
      setHasError(true);
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      const deleteResult: deleteServiceRequest = await graphQlClient.request(ServiceGraphQL.mutations.deleteService, {
        id,
      });
      if (deleteResult?.deleteService?.affected > 0) {
        setServices(services.filter((service: Service) => service.serviceId !== id));
        dispatch(appendActionMessage({ message: localeActionMessages['deleteSuccessful'], type: SnackType.SUCCESS }));
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    } finally {
      drawerClose();
    }
  };

  const handleDrawerSave = async (entityData: EntityData) => {
    if (!entityData.name || !entityData.description || !entityData.color) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['nameDescriptionColorRequired'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }
    const isEdit = drawerType === DrawerType.EDIT;

    if (isEdit && !entityData.areaId) {
      dispatch(
        appendActionMessage({
          message: localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }
    if (!entityData.name || !entityData.description || !entityData.color) {
      dispatch(
        appendActionMessage({
          message: localeActionMessages['fillServiceFields'],
          type: SnackType.ERROR,
        }),
      );
      return;
    }
    setSaving(true);
    try {
      const query = isEdit ? ServiceGraphQL.mutations.updateService : ServiceGraphQL.mutations.createService;
      const params = {
        service: {
          serviceId: entityData.serviceId,
          areaId: selectedArea?.areaId || entityData.areaId,
          name: entityData.name,
          description: entityData.description,
          color: entityData.color,
        },
      };
      if (!isEdit) {
        delete params?.service?.serviceId;
      }
      const data: createOrUpdateServiceRequest = await graphQlClient.request(query, params);
      if (data?.createService?.serviceId || data?.updateService?.serviceId) {
        if (isEdit && data?.updateService) {
          const existingService = services.find((s: Service) => s.serviceId === entityData.serviceId);
          const updatedService: Service = {
            ...data.updateService,
            processes: existingService?.processes || data.updateService.processes,
          };
          setServices(
            [
              ...services.filter((services: Service) => services.serviceId !== entityData.serviceId),
              updatedService,
            ].sort((a, b) => (a.order > b.order ? 1 : -1)),
          );
        } else {
          setServices([...services, data.createService as Service]);
        }
        drawerClose();
        dispatch(
          appendActionMessage({
            message: isEdit
              ? localeActionMessages['updateSuccessful']
              : localeActionMessages['serviceCreatedSuccessfully'],
            type: SnackType.SUCCESS,
          }),
        );
        setSaving(false);
      }
    } catch (e: any) {
      dispatch(
        appendActionMessage({
          message: e?.response?.errors[0]?.message || localeCommon['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  };

  const getIntegrations = async () => {
    try {
      const { calendar, storage } = checkForIntegrations(
        await graphQlClient.request(IntegrationsGraphQL.queries.getIntegrations),
      );
      setHasCalendar(calendar);
      setHasStorage(storage);
    } catch (e: any) {
      console.log('error', e);
    }
  };

  const servicesInArea = (
    <ItemWithSummaryDrag
      isGridView={isGridView}
      elements={services}
      elementIdName="serviceId"
      showDrag={true}
      hasCalendar={hasCalendar}
      hasStorage={hasStorage}
      recentlyDroppedId={recentlyDroppedId}
      handleDragEnd={handleDragEnd}
      drawerEditOpen={drawerEditOpen}
      handleCardClick={handleCardClick}
      cardsRefs={cardRefs}
    />
  );
  const areaWithServices = areas.sort().map((area: Area) => {
    return (
      <Box key={area.areaId} sx={{ scrollMargin: 100 }} ref={(el) => (cardRefs.current[area.areaId] = el)}>
        <Box className="flex items-center my-8">
          <Typography variant="h4semibold" className="mx-2">
            {area.name}
          </Typography>
        </Box>
        {
          <ItemWithSummaryDrag
            isGridView={isGridView}
            elements={services.filter((service) => service.areaId === area.areaId)}
            elementIdName="serviceId"
            showDrag={true}
            hasCalendar={hasCalendar}
            hasStorage={hasStorage}
            recentlyDroppedId={recentlyDroppedId}
            handleDragEnd={handleDragEnd}
            drawerEditOpen={drawerEditOpen}
            handleCardClick={handleCardClick}
            cardsRefs={cardRefs}
          />
        }
      </Box>
    );
  });

  const allAreasNavigation = (
    <div className='mb-28'>
      {areas.sort().map((area: Area) => {
        return (
          <div key={area.areaId}>
            <WolfHelperNavigation
              type={'title'}
              label={area.name}
              selected={selectedNavigationItem === area.areaId}
              onSelect={() => {
                scrollToCard(area.areaId);
              }}
            />
            {services
              .filter((service) => service.area?.areaId === area.areaId)
              .sort()
              .map((service: Service) => {
                return (
                  <div key={service.serviceId}>
                    <WolfHelperNavigation
                      key={service.serviceId}
                      type={'section'}
                      label={service.name}
                      selected={selectedNavigationItem === service.serviceId}
                      onSelect={() => {
                        scrollToCard(service.serviceId);
                      }}
                    />
                  </div>
                );
              })}
          </div>
        );
      })}
    </div>
  );

  const selectedNavigation = (
    <div className='mb-28'>
      {services.map((service: Service) => {
        return (
          <div key={service.serviceId}>
            <WolfHelperNavigation
              key={service.serviceId}
              type={'section'}
              label={service.name}
              selected={selectedNavigationItem === service.serviceId}
              onSelect={() => {
                scrollToCard(service.serviceId);
              }}
            />
          </div>
        );
      })}
    </div>
  );
  const scrollToCard = (id: string) => {
    setSelectedNavigationItem(id);

    if (cardRefs.current[id]) {
      cardRefs.current[id]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <>
      <Box className={`w-full mb-28`}>
        <Box className="sticky top-0 z-30 bg-backgroundUI">
          <GridListSubheader
            title={localeCommon['areas']}
            titleLink="/areas"
            subTitle={selectedArea?.name || localeCommon['allAreas']}
            subTitleLink={'/areas'}
            buttonText={role !== UserRoles.USER ? localeServices['addNew'] : null}
            isGridView={isGridView}
            onToggleView={handleToggleView}
            onDrawerOpen={drawerNewOpen}
            icon="service"
            iconColor={selectedArea?.color}
          />
        </Box>
        {autoOpenDrawer && !dataLoaded && (
          <Box className="flex justify-center items-center h-32">
            <CircularProgress size={24} />
            <Typography variant="body16semibold" className="ml-2">
              Loading...
            </Typography>
          </Box>
        )}
        {services.length === 0 ? (
          <Box className="flex justify-center items-center h-full w-full">
            <Typography variant="h4semibold">
              {hasError ? localeCommon['requestError'] : localeServices['emptyServiceMessage']}
            </Typography>
          </Box>
        ) : (
          <Box className="flex">
            <Box className="w-3/4">{selectedArea ? servicesInArea : areaWithServices}</Box>
            <Box className="w-1/5 fixed -right-1 overflow-y-scroll scrollbar-hide h-5/6">
              {selectedArea ? selectedNavigation : allAreasNavigation}
            </Box>
          </Box>
        )}
      </Box>

      <CustomDrawer
        isOpen={showDrawer}
        onClose={drawerClose}
        title={drawerType === 'edit' ? localeServices['edit'] : localeServices['addNew']}>
        <AddItemForm
          onClose={drawerClose}
          handleSave={handleDrawerSave}
          handleDelete={handleDelete}
          drawerType={drawerType}
          initialData={entityData}
          saving={saving}
          hasSelectedArea={!!selectedArea}
          isService={true}
          saveBtnText={drawerType === DrawerType.EDIT ? localeServices['editService'] : localeServices['createService']}
          parentColor={selectedArea?.color || areas.filter((area: Area) => area.areaId === entityData.areaId)[0]?.color}
          existingColors={services
            .filter((service: Service) => service.serviceId !== entityData.serviceId)
            .map((service: Service) => service.color)}></AddItemForm>
      </CustomDrawer>
    </>
  );
};

export default ServiceList;
